// Coordinates for Grup Xaluca properties + key landmarks of southern Morocco.
// Used by the homepage interactive map and any future location-aware features.

// HOTEL coords are DERIVED from the canonical `HOTEL_LOCATIONS` map in
// `hotelLocations.js` — a single source of truth prevents the coordinate
// drift we had when this file kept its own hand-typed approximations. The
// canonical file stores `coords: [lat, lng]`; we reshape to `{lat, lng}`
// so all downstream consumers (MapSection, PropertyCardMiniMap, etc.) keep
// working without a signature change.
import { HOTEL_LOCATIONS } from "./hotelLocations";

export const HOTEL_COORDS = Object.fromEntries(
  Object.entries(HOTEL_LOCATIONS)
    .filter(([, loc]) => Array.isArray(loc?.coords) && loc.coords.length === 2)
    .map(([slug, loc]) => [slug, { lat: loc.coords[0], lng: loc.coords[1] }])
);

// Major landmarks / destinations of southern Morocco — non-hotel context markers.
export const LANDMARKS = [
  {
    id: "erg-chebbi",
    name: { en: "Erg Chebbi Dunes", fr: "Dunes de l'Erg Chebbi", es: "Dunas de Erg Chebbi" },
    type: { en: "Sahara dunes · 150 m high", fr: "Dunes du Sahara · 150 m de haut", es: "Dunas del Sáhara · 150 m de altura" },
    desc: {
      en: "The most cinematic sand sea of Morocco — orange dunes rising to 150 m above the hammada, our base for camel caravans and bivouac nights.",
      fr: "La mer de sable la plus cinématographique du Maroc — dunes orange culminant à 150 m, notre point de départ des caravanes chamelières et des nuits en bivouac.",
      es: "El mar de arena más cinematográfico de Marruecos — dunas anaranjadas que se elevan a 150 m, nuestra base para caravanas en camello y noches en bivac.",
    },
    lat: 31.1488,
    lng: -3.9690,
  },
  {
    id: "ait-ben-haddou",
    name: { en: "Aït Ben Haddou", fr: "Aït Ben Haddou", es: "Aït Ben Haddou" },
    type: { en: "UNESCO ksar · 11th c.", fr: "Ksar UNESCO · XIᵉ s.", es: "Ksar UNESCO · s. XI" },
    desc: {
      en: "A fortified earthen village on the old caravan route — UNESCO since 1987, backdrop of dozens of films from Gladiator to Game of Thrones.",
      fr: "Un village fortifié en terre crue sur l'ancienne route caravanière — classé UNESCO depuis 1987, décor de Gladiator à Game of Thrones.",
      es: "Un ksar fortificado de tierra en la antigua ruta caravanera — UNESCO desde 1987, escenario de Gladiator a Juego de Tronos.",
    },
    lat: 31.0470,
    lng: -7.1303,
  },
  {
    id: "ouarzazate",
    name: { en: "Ouarzazate", fr: "Ouarzazate", es: "Ouarzazate" },
    type: { en: "Door of the desert · film city", fr: "Porte du désert · cité du cinéma", es: "Puerta del desierto · ciudad del cine" },
    desc: {
      en: "The southern hub — Taourirt Kasbah, the legendary Atlas Studios and the gateway to the Drâa and Dadès valleys.",
      fr: "Le pivot du sud — Kasbah Taourirt, les célèbres Atlas Studios et la porte des vallées du Drâa et du Dadès.",
      es: "El pivote del sur — Kasbah Taourirt, los míticos Atlas Studios y la puerta a los valles del Drâa y el Dadès.",
    },
    lat: 30.9189,
    lng: -6.8934,
  },
  {
    id: "dades-gorges",
    name: { en: "Dadès Gorges", fr: "Gorges du Dadès", es: "Gargantas del Dadès" },
    type: { en: "Red canyons · monkey-fingers", fr: "Canyons rouges · doigts de singe", es: "Cañones rojos · dedos de mono" },
    desc: {
      en: "Red sandstone canyons folded by erosion — the famous monkey-finger rock formations and a road of 365 hairpin turns.",
      fr: "Canyons de grès rouge sculptés par l'érosion — les célèbres « doigts de singe » et une route aux 365 virages.",
      es: "Cañones de arenisca roja esculpidos por la erosión — los famosos «dedos de mono» y una carretera de 365 curvas.",
    },
    lat: 31.5530,
    lng: -5.9580,
  },
  {
    id: "todra-gorges",
    name: { en: "Todra Gorges", fr: "Gorges du Todra", es: "Gargantas del Todra" },
    type: { en: "300 m limestone cliffs", fr: "Falaises calcaires de 300 m", es: "Acantilados calcáreos de 300 m" },
    desc: {
      en: "A 600 m-deep limestone chasm where the Todra river squeezes between sheer 300 m cliffs — a paradise for climbers.",
      fr: "Une gorge calcaire de 600 m de profondeur où l'oued Todra se faufile entre des falaises de 300 m — paradis des grimpeurs.",
      es: "Un cañón calcáreo de 600 m de profundidad por donde el río Todra se cuela entre acantilados de 300 m — paraíso de escaladores.",
    },
    lat: 31.5870,
    lng: -5.5970,
  },
  {
    id: "drâa-valley",
    name: { en: "Drâa Valley", fr: "Vallée du Drâa", es: "Valle del Drâa" },
    type: { en: "Palm groves · 200 km", fr: "Palmeraies · 200 km", es: "Palmerales · 200 km" },
    desc: {
      en: "The longest oasis-river of Morocco — 200 km of palm groves, mud-brick villages and the road to Mhamid, last stop before the Sahara.",
      fr: "Le plus long oued-oasis du Maroc — 200 km de palmeraies, villages en terre crue et route vers Mhamid, dernier village avant le Sahara.",
      es: "El más largo río-oasis de Marruecos — 200 km de palmerales, pueblos de tierra cruda y la carretera a Mhamid, última parada antes del Sáhara.",
    },
    lat: 30.3290,
    lng: -6.2920,
  },
  {
    id: "marrakech",
    name: { en: "Marrakech", fr: "Marrakech", es: "Marrakech" },
    type: { en: "The Red City · cultural gateway", fr: "La Ville rouge · porte culturelle", es: "La Ciudad roja · puerta cultural" },
    desc: {
      en: "The cultural gateway to the south. Jemaa el-Fna, the souks of the Medina, the Atlas peaks already visible from the rooftops.",
      fr: "La porte culturelle du sud. Jemaa el-Fna, les souks de la médina, et déjà les sommets de l'Atlas visibles depuis les terrasses.",
      es: "La puerta cultural al sur. Jemaa el-Fna, los zocos de la medina y las cumbres del Atlas ya visibles desde las azoteas.",
    },
    lat: 31.6295,
    lng: -7.9811,
  },
  {
    id: "fes",
    name: { en: "Fes", fr: "Fès", es: "Fez" },
    type: { en: "Imperial city · 1 200 yrs old", fr: "Ville impériale · 1 200 ans", es: "Ciudad imperial · 1 200 años" },
    desc: {
      en: "The oldest imperial city — medieval medina of Fes el-Bali, leather tanneries, and the historic Madrasa Bou Inania.",
      fr: "La plus ancienne des villes impériales — médina médiévale de Fès el-Bali, tanneries et historique médersa Bou Inania.",
      es: "La más antigua de las ciudades imperiales — medina medieval de Fes el-Bali, curtidurías y la histórica madraza Bou Inania.",
    },
    lat: 34.0181,
    lng: -5.0078,
  },
  {
    id: "midelt-cedar",
    name: { en: "Cedar forests of Midelt", fr: "Cédraies de Midelt", es: "Cedrales de Midelt" },
    type: { en: "Middle Atlas · 2 000 m altitude", fr: "Moyen Atlas · 2 000 m d'altitude", es: "Atlas Medio · 2 000 m de altitud" },
    desc: {
      en: "Vast Atlas cedar forests where Barbary macaques roam at over 2 000 m — the green threshold between Fes and the desert.",
      fr: "Vastes forêts de cèdres de l'Atlas où vivent les magots à plus de 2 000 m — seuil vert entre Fès et le désert.",
      es: "Vastos bosques de cedros del Atlas donde habitan los macacos de Berbería a más de 2 000 m — umbral verde entre Fez y el desierto.",
    },
    lat: 32.9120,
    lng: -4.7350,
  },
  {
    id: "mgoun",
    name: { en: "M'Goun Valley", fr: "Vallée du M'Goun", es: "Valle del M'Goun" },
    type: { en: "Atlas peak · 4 071 m", fr: "Sommet de l'Atlas · 4 071 m", es: "Pico del Atlas · 4 071 m" },
    desc: {
      en: "The wild high valleys around Morocco's third-highest peak (4 071 m) — accessed on foot or by mule from our Ifess refuge.",
      fr: "Les vallées sauvages autour du troisième sommet du Maroc (4 071 m) — accessibles à pied ou à dos de mulet depuis notre refuge d'Ifess.",
      es: "Los valles salvajes en torno al tercer pico de Marruecos (4 071 m) — accesibles a pie o en mula desde nuestro refugio de Ifess.",
    },
    lat: 31.5167,
    lng: -6.4333,
  },
  {
    id: "lake-sidi-ali",
    name: { en: "Lake Aguelmame Sidi Ali", fr: "Lac Aguelmame Sidi Ali", es: "Lago Aguelmame Sidi Ali" },
    type: { en: "Highest lake in Morocco · 2 080 m", fr: "Plus haut lac du Maroc · 2 080 m", es: "Lago más alto de Marruecos · 2 080 m" },
    desc: {
      en: "A volcanic lake nestled at 2 080 m in the Middle Atlas — the highest in Morocco, surrounded by cedar woods and trout streams.",
      fr: "Un lac volcanique blotti à 2 080 m dans le Moyen Atlas — le plus haut du Maroc, cerné de cèdres et de ruisseaux à truite.",
      es: "Un lago volcánico a 2 080 m en el Atlas Medio — el más alto de Marruecos, rodeado de cedros y arroyos truchícolas.",
    },
    lat: 33.0586,
    lng: -5.0008,
  },
  {
    id: "merzouga",
    name: { en: "Merzouga village", fr: "Village de Merzouga", es: "Pueblo de Merzouga" },
    type: { en: "Saharan oasis · base of Erg Chebbi", fr: "Oasis saharienne · base de l'Erg Chebbi", es: "Oasis sahariano · base del Erg Chebbi" },
    desc: {
      en: "The Saharan oasis town at the foot of Erg Chebbi — starting point of every dune trek and our gateway to the desert.",
      fr: "Le village-oasis au pied de l'Erg Chebbi — départ de chaque trek de dunes et notre porte d'entrée du désert.",
      es: "El pueblo-oasis al pie de Erg Chebbi — punto de partida de cada trekking por dunas y nuestra puerta al desierto.",
    },
    lat: 31.0998,
    lng: -4.0103,
  },
];

// Bounding box useful for fitBounds — covers Marrakech / Fes / Sahara.
export const MAP_BOUNDS = [
  [29.5, -8.5], // SW
  [34.5, -3.0], // NE
];

export const MAP_CENTER = [31.5, -5.6];
